import { Plan } from '@/src/components/desktop/srp/fareFamily/types'
import { LESS_THAN_THIRTY, LESS_THAN_ZERO } from '@/src/constants/srp'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import { DeviceType } from '@/src/types/deviceType'
import { FlightCard } from '@/src/types/flight'
import { differenceInMinutes } from '@/src/utils/date'
import { getRouteSummaries } from '@/src/utils/srp/routeUtils'
import { checkingFlightsValidTimings } from '@/src/utils/srp/RT/check-valid-timings'
import {
    airlineFilterPriceModifier,
    priceRangeFilterPriceModifier,
    stopsFilterPriceModifier
} from '@/src/utils/srp/RT/filter-price-modier'
import { modifyPricesAndSortOfReturnLeg } from '@/src/utils/srp/RT/sort-modifier'
import { cardGrouping } from '@/src/utils/srp/searchUtils/cardGrouping'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useClearFilters } from '../../clearFilters'
import useUpdateSearchResultCache from '../../updateSearchResultCache'
import useSearchResults from '../../useSearchResults'
import useRTFareFamilyManager from '../RTFareFamilyManager'

export interface StepperStep {
    label: string
    completed: boolean
    active: boolean
}

export const useRTSolutionFlowManager = ({
    handleFlightBook,
    deviceType
}: {
    handleFlightBook?: (cards: FlightCard[], fareFamilyData?: (Plan | null)[]) => void
    deviceType: DeviceType
}) => {
    const locale = useLocale()
    const isArabic = locale === 'ar'
    const { showError } = useThemedToast()
    const tCommon = useTranslations('common')
    const updateSearchResultCache = useUpdateSearchResultCache()
    const { selectedLegSortedFilteredCards, selectedJourneyLeg, jsons, sectors, sid, showEmptyFlightScreen } =
        useSearchResults()

    const [selectedOnwardCard, setSelectedOnwardCard] = useState<FlightCard | null>(null)
    const selectedOnwardFareFamilyPlanRef = useRef<Plan | null>(null)
    const selectedOnwardFareFamilyPlan = selectedOnwardFareFamilyPlanRef.current

    const [returnLegsTransition, setReturnLegsTransition] = useState(false)
    const { isFilterApplied, handleClearAllFilters } = useClearFilters()
    const priceGroupEntries = useMemo(
        () => cardGrouping(selectedLegSortedFilteredCards, true),
        [selectedLegSortedFilteredCards]
    )

    const route = useMemo(() => {
        const sectionArr = Object.values(jsons?.secs || {})
        const route = getRouteSummaries(sectionArr, jsons?.ap || {}, isArabic)[selectedJourneyLeg]
        return route
    }, [jsons?.secs, jsons?.ap, selectedJourneyLeg, isArabic])

    const stepperSteps: StepperStep[] = useMemo(() => {
        return [
            {
                label: tCommon('chooseDepartureFlight'),
                completed: selectedJourneyLeg === 1,
                active: selectedJourneyLeg === 0
            },
            { label: tCommon('chooseReturnFlight'), completed: false, active: selectedJourneyLeg === 1 },
            { label: tCommon('reviewYourTrip'), completed: false, active: false }
        ]
    }, [selectedJourneyLeg])

    const changeJourneyLegTo = useCallback(
        (toBeChangeLeg: 0 | 1) => {
            if (toBeChangeLeg === 0) {
                selectedOnwardFareFamilyPlanRef.current = null
                setSelectedOnwardCard(null)

                if (isFilterApplied) handleClearAllFilters() //reset return leg filters when change leg to onward
            }

            updateSearchResultCache(currentData => {
                if (currentData) {
                    currentData.solutionFlowSelectedLeg = toBeChangeLeg
                }
                return currentData as NonNullable<typeof currentData>
            })

            //scroll to top of the page with smooth behavior
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            })
        },
        [updateSearchResultCache, isFilterApplied, handleClearAllFilters]
    )

    const handlePrepareReturnLegCards = useCallback(
        (selectedOnwardCard: FlightCard, selectedOnwardFareFamilyPlan: Plan | null) => {
            updateSearchResultCache(currentData => {
                if (currentData) {
                    modifyPricesAndSortOfReturnLeg(currentData, selectedOnwardCard, selectedOnwardFareFamilyPlan)
                    priceRangeFilterPriceModifier(currentData, 1)
                    airlineFilterPriceModifier(currentData, 1)
                    stopsFilterPriceModifier(currentData, 1)
                }
                return currentData as NonNullable<typeof currentData>
            })
        },
        [updateSearchResultCache, selectedOnwardFareFamilyPlan]
    )

    const [cardDetails, setCardDetails] = useState<{ cards: FlightCard[]; showBaggage: boolean, selectedFareFamilyPlans?: (Plan | null)[] } | null>(null)
    const handleOpenViewDetails = useCallback((cards: FlightCard[], showBaggage: boolean = false, selectedFareFamilyPlans?: (Plan | null)[]) => {
        setCardDetails({ cards, showBaggage, selectedFareFamilyPlans })
    }, [])

    const handleCloseViewDetails = useCallback(() => {
        setCardDetails(null)
    }, [])

    const selectCard = useCallback(
        (card: FlightCard, selectedFareFamilyPlan: Plan | null) => {
            if (selectedJourneyLeg === 0) {
                handleCloseViewDetails()
                setReturnLegsTransition(true)
                selectedOnwardFareFamilyPlanRef.current = selectedFareFamilyPlan
                setSelectedOnwardCard(card)
                setTimeout(() => {
                    handlePrepareReturnLegCards(card, selectedFareFamilyPlan)
                    setReturnLegsTransition(false)
                    changeJourneyLegTo(1)
                }, 1000)
            } else {
                const filteredFlightTimeDiff = differenceInMinutes(
                    card.firstDeparture.timestamp,
                    selectedOnwardCard!.lastArrival.timestamp
                )
                let timeDiff = checkingFlightsValidTimings(filteredFlightTimeDiff)

                if (timeDiff === LESS_THAN_ZERO) {
                    showError(tCommon('flightTimeOverlapError'), {
                        id: 'flightTimeOverlapError'
                    })

                    return
                } else if (timeDiff === LESS_THAN_THIRTY) {
                    showError(tCommon('thirtyMinsOverlapError'), {
                        id: 'thirtyMinsOverlapError'
                    })
                    return
                }

                //if any leg fare selected through fare family popup then trigger booking directly without opening combine flight details [START]

                // if (selectedOnwardFareFamilyPlan || selectedFareFamilyPlan) {
                //     //trigger booking
                //     const cards = [selectedOnwardCard!, card]
                //     handleFlightBook?.(cards, [selectedOnwardFareFamilyPlan, selectedFareFamilyPlan])
                //     return
                // }

                // [END]

                //for now just trigger booking from return leg select [start]
                // const cards = [selectedOnwardCard!, card]
                // handleCloseViewDetails()
                // handleFlightBook?.(cards, true)

                // return
                //---------------- [end]

                //leg is 1 but details are not open  then open details for both cards
                if (!cardDetails) {
                    handleOpenViewDetails([selectedOnwardCard!, card], true, [selectedOnwardFareFamilyPlan, selectedFareFamilyPlan])
                }

                //or details are open for one card then close details and open details for both cards
                else if (cardDetails.cards.length === 1) {
                    handleCloseViewDetails()
                    setTimeout(() => {
                        handleOpenViewDetails([selectedOnwardCard!, card], true, [selectedOnwardFareFamilyPlan, selectedFareFamilyPlan])
                    }, 500)
                } else {
                    //trigger booking
                    const cards = [selectedOnwardCard!, card]
                    const selectedFareFamilyPlans = Boolean(selectedOnwardFareFamilyPlan || selectedFareFamilyPlan) ? [selectedOnwardFareFamilyPlan, selectedFareFamilyPlan] : undefined
                    handleCloseViewDetails()
                    handleFlightBook?.(cards, selectedFareFamilyPlans)
                }
            }
        },
        [
            selectedJourneyLeg,
            changeJourneyLegTo,
            selectedOnwardCard,
            handleCloseViewDetails,
            handleOpenViewDetails,
            cardDetails,
            showError,
            tCommon,
            handleFlightBook,
        ]
    )

    const {
        fareFamilyPopup,
        handleOpenFareFamily,
        isFareFamilyLoading,
        closeFareFamilyPopup,
        handleSelectFareFamilyPlan,
        fareFamilyLoadingMessage
    } = useRTFareFamilyManager(selectCard)

    const handleSelectCard = useCallback(
        async (card: FlightCard) => {
            if (cardDetails?.cards.length === 2) {
                selectCard(card, cardDetails?.selectedFareFamilyPlans?.[1] || null)

                return
            }
            handleCloseViewDetails()
            await handleOpenFareFamily(card, deviceType, selectedOnwardFareFamilyPlan, selectedOnwardCard)
        },
        [deviceType, handleOpenFareFamily, handleCloseViewDetails, selectedOnwardFareFamilyPlan, selectedOnwardCard, cardDetails?.cards.length]
    )

    return {
        priceGroupEntries,
        route,
        stepperSteps,
        handleSelectCard,
        returnLegsTransition,
        selectedOnwardCard,
        changeJourneyLegTo,
        handleOpenViewDetails,
        cardDetails,
        handleCloseViewDetails,
        sectors,
        sid,
        jsons,
        showEmptyFlightScreen,
        selectedJourneyLeg,
        fareFamilyPopup,
        isFareFamilyLoading,
        fareFamilyLoadingMessage,
        closeFareFamilyPopup,
        handleSelectFareFamilyPlan,
        selectedOnwardFareFamilyPlan
    }
}
